(() => {
  'use strict';
  if (customElements.get('bc-product-rail')) return;
  class BosscoProductRail extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.bindRail();
      if (this.dataset.recommendationsUrl) this.loadRecommendations();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.railController?.abort();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    bindRail() {
      this.resizeObserver?.disconnect();
      this.railController?.abort();
      this.railController = new AbortController();
      this.track = this.querySelector('[data-bc-rec-track]');
      this.controls = this.querySelector('[data-bc-rec-controls]');
      if (!this.track || !this.controls) return;
      this.previous = this.querySelector('[data-bc-rec-prev]');
      this.next = this.querySelector('[data-bc-rec-next]');
      const options = { signal: this.railController.signal };
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.updateControls(); });
      }, { ...options, passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.track);
      } else window.addEventListener('resize', () => this.updateControls(), options);
      this.bindFilters(options);
      this.updateControls();
    }

    bindFilters(options) {
      this.cards = Array.from(this.track.children);
      this.filters = Array.from(this.querySelectorAll('[data-bc-rec-filter]'));
      const bar = this.querySelector('[data-bc-rec-filterbar]');
      if (!bar || this.filters.length < 3) return;
      bar.hidden = false;
      this.filters.forEach((button, index) => {
        button.addEventListener('click', () => this.filter(button.dataset.bcRecFilter), options);
        button.addEventListener('keydown', event => {
          let nextIndex;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.filters.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.filters.length) % this.filters.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = this.filters.length - 1;
          if (nextIndex === undefined) return;
          event.preventDefault();
          this.filters[nextIndex].focus();
        }, options);
      });
      document.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('[data-bc-rec-category]');
        if (!card || !this.contains(card)) return;
        if (card.hidden) this.filter(card.dataset.bcRecCategory);
        card.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
      }, options);
    }

    filter(category) {
      let count = 0;
      this.cards.forEach(card => {
        card.hidden = category !== 'all' && card.dataset.bcRecCategory !== category;
        if (!card.hidden) {
          count += 1;
          card.classList.remove('bc-in-view');
          card.style.setProperty('--bc-reveal-delay', `${((count - 1) % 3) * 70}ms`);
        }
      });
      this.filters.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.bcRecFilter === category)));
      const status = this.querySelector('[data-bc-rec-filter-status]');
      if (status) status.textContent = `${count} ${count === 1 ? 'accessory' : 'accessories'}`;
      this.track.scrollTo({ left: 0, behavior: 'auto' });
      this.updateControls();
      window.BosscoRollerLanding?.scan();
      if (!window.BosscoRollerLanding) this.cards.forEach(card => card.classList.add('bc-in-view'));
    }

    updateControls() {
      if (!this.track?.isConnected) return;
      const overflow = this.track.scrollWidth - this.track.clientWidth;
      const position = Math.abs(this.track.scrollLeft);
      this.controls.hidden = overflow < 2;
      this.previous.disabled = position < 2;
      this.next.disabled = position >= overflow - 2;
    }

    move(direction) {
      const card = Array.from(this.track.children).find(item => !item.hidden);
      if (!card) return;
      const styles = getComputedStyle(this.track);
      const step = card.getBoundingClientRect().width + parseFloat(styles.columnGap || 0);
      const visibleCards = Math.max(1, Math.floor((this.track.clientWidth + 2) / step));
      const rtl = styles.direction === 'rtl' ? -1 : 1;
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const paused = document.documentElement.classList.contains('bc-landing-page') && !document.documentElement.classList.contains('bc-motion-on');
      this.track.scrollBy({ left: step * visibleCards * direction * rtl, behavior: reduced || paused ? 'auto' : 'smooth' });
    }

    async loadRecommendations() {
      const url = this.dataset.recommendationsUrl;
      try {
        const response = await fetch(url, { signal: this.controller.signal, credentials: 'same-origin' });
        if (!response.ok) return;
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = documentFragment.querySelector('bc-product-rail [data-bc-rec-content]');
        if (!this.isConnected || !content) return;
        delete this.dataset.recommendationsUrl;
        if (Number(content.dataset.productCount) === 0) return;
        this.querySelector('[data-bc-rec-content]').replaceWith(content);
        this.hidden = false;
        this.bindRail();
        window.BosscoRollerLanding?.scan();
      } catch (error) {
        if (error.name !== 'AbortError' && window.Shopify?.designMode) console.warn('Bossco product recommendations could not load. The fallback collection remains available.');
      }
    }
  }
  customElements.define('bc-product-rail', BosscoProductRail);
})();
